// src/Views/HomePage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Sun, Moon } from 'lucide-react';
import leafIcon from '../assets/leaf_1.svg';
// import fridgeFriendImg from '../assets/images/fridgefriend.jpg';
// import billifyImg from '../assets/images/billify.jpg';
// import companionImg from '../assets/images/companion.jpeg';
import '../styles/HomePage.css';
import '../styles/Projects.css';

// Snapshot imports
import snapshot1 from '../assets/images/snapshots/9man.JPG';
import snapshot2 from '../assets/images/snapshots/10k.JPEG';
import snapshot3 from '../assets/images/snapshots/pretty.jpg';
import snapshot4 from '../assets/images/snapshots/family.JPG';
import snapshot5 from '../assets/images/snapshots/icecream.jpg';
import snapshot9 from '../assets/images/snapshots/piano.jpg';
import snapshot10 from '../assets/images/snapshots/rcm.JPG';
import snapshot11 from '../assets/images/snapshots/shopify.JPEG';
import snapshot12 from '../assets/images/snapshots/palau.jpg';
import snapshot13 from '../assets/images/snapshots/21k.jpg';
import snapshotRun from '../assets/images/snapshots/run.png';
import snapshotMedicineLake from '../assets/images/snapshots/medicinelake.jpg';
import snapshotKaroke from '../assets/images/snapshots/karoke.jpg';
import snapshotBc from '../assets/images/snapshots/bc.jpg';
import snapshotWheel from '../assets/images/snapshots/wheel.jpg';
import snapshotRainer from '../assets/images/snapshots/rainer.jpg';
import snapshotManhedge from '../assets/images/snapshots/manhedge.jpg';

// Preload images for better caching
const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};

// Array of all snapshot images
const snapshotImages = [
  snapshotRun, snapshotMedicineLake, snapshotKaroke,
  snapshotWheel, snapshotManhedge,
  snapshot1, snapshot2, snapshot3, snapshot4, snapshotRainer, snapshot13, snapshot5,
  snapshotBc, snapshot9, snapshot10, snapshot11, snapshot12
];

// const projects = [
//   {
//     id: 'fridgefriend',
//     title: 'FRIDGEFRIEND',
//     description: 'Transform your leftovers into delicious meals',
//     image: fridgeFriendImg,
//     longDescription: 'A smart kitchen companion that helps you reduce food waste by suggesting recipes based on your available ingredients.',
//     website: 'https://your-fridgefriend-url.com'
//   },
//   {
//     id: 'billify',
//     title: 'BILLIFY',
//     description: 'Simplify your expense tracking and splitting',
//     image: billifyImg,
//     longDescription: 'Your comprehensive solution for expense tracking and bill splitting among friends and roommates.',
//     website: 'https://your-billify-url.com'
//   },
//   {
//     id: 'companion',
//     title: 'COMPANION',
//     description: 'Your AI-powered mental health buddy',
//     image: companionImg,
//     longDescription: 'An AI companion that provides mental health support and guidance.',
//     website: 'https://your-companion-url.com'
//   }
// ];

const ProjectModal = ({ project, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2 className="modal-title">{project.title}</h2>
        <p className="modal-details">{project.description}</p>
        <img 
          src={project.image} 
          alt={project.title} 
          className="modal-image"
          loading="lazy"
          decoding="async"
        />
        <p className="modal-details">{project.longDescription}</p>
        {project.website && (
          <a 
            href={project.website}
            className="website-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            WEBSITE
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="arrow-icon"
            >
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
        )}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [isDark, setIsDark] = useState(() => {
    // Read from localStorage on initial load
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [selectedProject, setSelectedProject] = useState(null);

  // Preload images on component mount for better caching
  useEffect(() => {
    // Preload critical images immediately
    preloadImage(leafIcon);
    
    // Preload snapshot images with a slight delay to not block initial render
    const preloadTimer = setTimeout(() => {
      snapshotImages.forEach(img => preloadImage(img));
    }, 100);

    return () => clearTimeout(preloadTimer);
  }, []);

  // Persist dark mode to localStorage and update body background
  useEffect(() => {
    // Save to localStorage whenever dark mode changes
    localStorage.setItem('darkMode', isDark.toString());
    
    if (isDark) {
      document.body.style.backgroundColor = '#111827';
      document.documentElement.style.backgroundColor = '#111827';
    } else {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isDark]);

  return (
    <div className={`container ${isDark ? 'dark' : ''}`}>
      <header className="header">
        <nav className="nav">
          <Link to="/" className="nav-link">home</Link>
          <Link to="/experience" className="nav-link">experience</Link>
          <Link to="/projects" className="nav-link">projects</Link>
          <a href="/design" className="nav-link">design</a>
          <button 
            onClick={() => setIsDark(!isDark)}
            className="nav-link theme-toggle"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </nav>
        </header>
        
      

      <main className="main-content">
      <div className="header-spacing flex justify-between items-center">
          <h1 className="text-5xl font-medium">LUCY QI</h1>
          <img 
            src={leafIcon} 
            alt="Leaf icon" 
            className="w-16 h-16" 
            width="64"
            height="64"
            fetchPriority="high"
          />
        </div>
        <p>i'm an engineer and artist who loves crafting meaningful, human-centered digital experiences.
        
        </p>


        <p>
        currently, i'm a cs student at the university of waterloo and a sde intern at <a href="https://aws.amazon.com/" 
            className="text-emerald-600 hover:underline font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >amazon web services</a>, working on the dynamodb capacity management team. i also run a studygram on instagram (35k followers) and a small art business ($5k+ profit).
        </p>

        <p>
          in my free time you'll probably find me <a 
            href="https://www.goodreads.com/user/show/60113847-lucy-qi" 
            className="text-emerald-600 hover:underline font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            exploring my ever-growing reading list
          </a>, running a half marathon, playing volleyball, or sketching to my endless{" "}
          <a 
            href="https://open.spotify.com/user/dolphin3754" 
            className="text-emerald-600 hover:underline font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            spotify playlists
          </a>.
        </p>
      </main>

      
      <div className="social-links">
        <a 
          href="https://github.com/lucyqqi"
          className="social-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Github className="w-6 h-6" />
          <span className="sr-only">GitHub</span>
        </a>
        <a 
          href="https://www.linkedin.com/in/lucyqqi/"
          className="social-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Linkedin className="w-6 h-6" />
          <span className="sr-only">LinkedIn</span>
        </a>
        {/*<a 
          href="https://drive.google.com/file/d/1wb0R5-jmrF2GsEAjw6u-gul9PMf_B7ts/view?usp=sharing"
          className="social-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FileText className="w-6 h-6" />
          <span className="sr-only">Resume</span>
        </a> */}
      </div>
      

       {/* <section id="projects" className="projects-content">
        <h2 className="page-title">recent projects</h2>
        <br></br>
        <div className="projects-grid">
          {projects.map(project => (
            <div 
              key={project.id}
              className="project-card"
              onClick={() => setSelectedProject(project)}
            >
              <img 
                src={project.image} 
                alt={`${project.title} Project`} 
                className="project-image"
              />
              <h2 className="project-title">{project.title}</h2>
              <p className="project-description">{project.description}</p>
            </div>
          ))}
        </div>

        <div className="separator-container">
          <div className="separator-wrapper">
            <div className="separator" />
            <Link 
              to="/projects"
              className="view-all-button"
              onClick={() => window.scrollTo(0, 0)}
            >
              View All Projects
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </div>
        </div>
      </section> */}
      

      <section id="snapshots" className="snapshots-section">
        <h2 className="page-title">SNAPSHOTS</h2>
        <br></br>
        <div className="snapshot-strip">
          <div className="snapshot-track">
            {[...Array(2)].map((_, arrayIndex) => (
              snapshotImages.map((image, index) => {
                const uniqueKey = `${arrayIndex}-${index}`;
                return (
                  <div key={uniqueKey} className="snapshot-item">
                    <img 
                      src={image} 
                      alt={`Snapshot ${index + 1}`}
                      loading="lazy"
                      width="300"
                      height="300"
                      decoding="async"
                    />
                  </div>
                );
              })
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <ProjectModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}

      <footer className="footer">
      <p>made with <span className="highlight">♥</span> in <span className="highlight">toronto</span></p>
      </footer>
      <br></br>
    </div>
  );
};

export default HomePage;

